import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { IMAGES, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Business() {
  const { t } = useLang();
  usePageMeta(t("meta.business")[0], t("meta.business")[1]);
  const exposures = t("businessPage.exposures");
  const factors = t("businessPage.factors");
  return (
    <>
      <PageHero
        overline={t("businessPage.heroOverline")}
        title={t("businessPage.heroTitle")}
        sub={t("businessPage.heroSub")}
        testId="business"
      />

      <section className="bg-white py-16 sm:py-24" aria-labelledby="business-detail-heading" data-testid="business-detail-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <Reveal>
                <h2 id="business-detail-heading" className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {t("businessPage.detailHeading")}
                </h2>
              </Reveal>
              <Reveal delay={1}>
                <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="business-intro-copy">
                  {t("businessPage.intro")}
                </p>
              </Reveal>
              <Reveal delay={2}>
                <h3 className="font-heading mt-8 text-sm font-bold uppercase tracking-[0.2em] text-[#0F4C5C]">
                  {t("businessPage.exposuresHeading")}
                </h3>
                <ul className="mt-4 space-y-2.5" data-testid="exposures-list">
                  {exposures.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#D97706]" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={3}>
                <div className="mt-8 bg-[#D97706] p-8 text-white shadow-lg shadow-[#D97706]/20" data-testid="premium-financing-card">
                  <h3 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">{t("businessPage.financingTitle")}</h3>
                  <p className="mt-3 text-lg font-medium leading-relaxed text-white/90">
                    {t("businessPage.financingSub")}
                  </p>
                </div>
              </Reveal>
            </div>
            <div>
              <Reveal delay={1}>
                <img
                  src={IMAGES.business}
                  alt={t("businessPage.imageAlt")}
                  className="h-[320px] w-full object-cover shadow-xl"
                  loading="lazy"
                  data-testid="business-image"
                />
              </Reveal>
              <Reveal delay={2}>
                <div className="mt-6 border border-[#0F4C5C]/10 bg-[#EEF6FC] p-7" data-testid="premium-factors-card">
                  <h3 className="font-heading text-lg font-bold text-slate-900">{t("businessPage.factorsTitle")}</h3>
                  <p className="mt-2 text-sm text-slate-600">{t("businessPage.factorsIntro")}</p>
                  <ul className="mt-4 grid gap-2.5 sm:grid-cols-2" data-testid="premium-factors-list">
                    {factors.map((factor) => (
                      <li key={factor} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C5C]" aria-hidden="true" />
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={3}>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/quote" className="inline-flex items-center gap-2 bg-[#0F4C5C] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#1A365D]" data-testid="business-quote-btn">
                    {t("common.requestQuote")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a href={SITE.phone1Href} className="inline-flex items-center gap-2 border-2 border-[#0F4C5C] px-6 py-3 text-sm font-bold uppercase tracking-wider text-[#0F4C5C] transition-colors duration-200 hover:bg-[#0F4C5C] hover:text-white" data-testid="business-call-btn">
                    <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone1}
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
